import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ActionProps } from 'react-querybuilder';
import { apiDelete, apiGet, apiPost } from "../../api";
import { forgetFilter } from "./filterStorage";

interface SavedFilter {
    id: number;
    name: string;
    rules: Array<any>;
}

const QUERY_SAVED_FILTERS_TRUNCATE_KEY = 'query_saved_filters_truncate';
const truncateText = (text: string, charNumbers: number = 20) =>
    text?.length > charNumbers ? text.slice(0, charNumbers) + '\u2026' : text;

const SaveIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
        <polyline points="17 21 17 13 7 13 7 21" />
        <polyline points="7 3 7 8 15 8" />
    </svg>
);

const SearchIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <path d="M21 21L16.65 16.65" />
    </svg>
);

const TrashIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
    </svg>
);

const ChevronDownIcon = () => (
    <svg width="12" height="7" viewBox="0 0 12 7" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const AddGroupAction = ({ props }: { props: ActionProps }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [showTruncated, setShowTruncated] = useState(() => {
        const saved = localStorage.getItem(QUERY_SAVED_FILTERS_TRUNCATE_KEY);
        return saved === null ? true : saved === 'true';
    });
    const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
    const [quickSearch, setQuickSearch] = useState<string>(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get("q") || "";
    });
    const [savedFilterName, setSavedFilterName] = useState("");
    const [filterSearch, setFilterSearch] = useState("");
    const dropdownRef = useRef<HTMLDivElement>(null);
    const errorsRef = useRef<HTMLDivElement>(null);

    const toggleTruncate = () => {
        setShowTruncated((prev) => {
            const newValue = !prev;
            localStorage.setItem(QUERY_SAVED_FILTERS_TRUNCATE_KEY, JSON.stringify(newValue));
            return newValue;
        });
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        apiGet("/api/configuration/query_history/", { "content_type_id": props.context.contentType }).then((response: any) => {
            setSavedFilters(response);
        });
    }, [props.context.contentType]);

    useEffect(() => {
        if (!showDropdown) setFilterSearch("");
    }, [showDropdown]);

    const visibleFilters = useMemo(() => {
        const q = filterSearch.trim().toLowerCase();
        if (!q) return savedFilters;
        return savedFilters.filter((f) => f.name.toLowerCase().includes(q));
    }, [savedFilters, filterSearch]);

    function handleClickSave(event: any) {
        const filterNameInput = document.getElementById('filterNameInput') as HTMLInputElement;

        if (!savedFilterName) {
            filterNameInput?.classList.add('is-invalid');
            setTimeout(() => filterNameInput?.classList.remove('is-invalid'), 2000);
            return;
        }

        if (errorsRef.current) {
            errorsRef.current.innerHTML = "";
        }

        apiPost("/api/configuration/query_history/", {
            "rules": props.schema.getQuery(),
            "name": savedFilterName,
            "content_type": props.context.contentType
        }).then((response: any) => {
            setSavedFilters([...savedFilters, response]);
            setSavedFilterName("");
            setShowDropdown(false);
        }).catch((reason: any) => {
            event.preventDefault();
            if (errorsRef.current) {
                const params = reason?.params || {};
                const errorMessages = Object.entries(params)
                    .map(([, messages]: [string, any]) => `<div>${(messages as string[]).join(", ")}</div>`)
                    .join("");
                errorsRef.current.innerHTML = errorMessages;
            }
        });
    }

    function handleDeleteFilter(id: number) {
        apiDelete(`/api/configuration/query_history/${id}`).then(() => {
            setSavedFilters(savedFilters.filter(filter => filter.id !== id));
            if (forgetFilter(props.context.contentType, id)) {
                props.context.onResetActiveFilter?.();
            }
        });
    }

    const loadSavedFilter = (filterId: number) => {
        const foundFilter = savedFilters.find(filter => filter.id === filterId);
        if (foundFilter) {
            props.context.setQuery(foundFilter.rules);
            props.context.onSelectActiveFilter?.(foundFilter);
            props.context.onApply(foundFilter.rules, quickSearch);
        }
        setShowDropdown(false);
    };

    const handleAddGroup = (e: React.MouseEvent) => {
        props.context.handleShowRules(true);
        props.handleOnClick(e);
    };

    // Nested groups (level > 0): just render the add group button
    if (props.level !== 0) {
        return (
            <button
                type="button"
                className="rd-btn rd-btn--outline rd-btn--secondary rd-btn--medium"
                onClick={handleAddGroup}
                title={props.title}
                disabled={!props.context.fieldsReady}
            >
                {props.label}
            </button>
        );
    }

    const isCollapsed = props.context.isCollapsed;
    const canApply = !props.context.isLoading && props.context.fieldsReady;
    const applyFilter = () => {
        if (!canApply) {
            return;
        }
        props.context.onApply(props.context.query, quickSearch);
    };

    const handleQuickSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== "Enter" || e.repeat || e.nativeEvent.isComposing) {
            return;
        }
        e.preventDefault();
        applyFilter();
    };

    // Level 0: render inline (Fragment) so all elements stay in ruleGroup-header flex row
    return (
        <>
            {/* Add group button */}
            <button
                type="button"
                className="rd-btn rd-btn--outline rd-btn--secondary rd-btn--medium"
                onClick={handleAddGroup}
                disabled={!props.context.fieldsReady}
            >
                {props.label}
            </button>

            {/* Save/load button with dropdown */}
            <div className="qf-toolbar__save-wrapper" ref={dropdownRef}>
                <button
                    type="button"
                    className="qf-toolbar__icon-btn qf-toolbar__icon-btn--save"
                    onClick={() => setShowDropdown(!showDropdown)}
                    title="Сохранить фильтр"
                    disabled={!props.context.fieldsReady}
                >
                    <SaveIcon />
                </button>

                {showDropdown && (
                    <div className="qf-save-dropdown">
                        <div className="qf-save-dropdown__header">Избранные фильтры</div>

                        {savedFilters.length > 0 && (
                            <div className="qf-save-dropdown__search">
                                <div className="qf-save-dropdown__search-wrapper">
                                    <input
                                        type="text"
                                        className="qf-save-dropdown__search-input"
                                        placeholder="Поиск"
                                        value={filterSearch}
                                        onChange={(e) => setFilterSearch(e.target.value)}
                                    />
                                    <span className="qf-save-dropdown__search-icon">
                                        <SearchIcon />
                                    </span>
                                </div>
                            </div>
                        )}

                        {savedFilters.length > 0 && (
                            <div className="qf-save-dropdown__truncate-toggle">
                                <button type="button" onClick={toggleTruncate}>
                                    {showTruncated ? 'Показывать полностью имена фильтров' : 'Скрыть имена фильтров'}
                                </button>
                            </div>
                        )}

                        <div className="qf-save-dropdown__list">
                            {visibleFilters.map(filter => (
                                <div className="qf-save-dropdown__item" key={filter.id}>
                                    <span
                                        className="qf-save-dropdown__item-name"
                                        onClick={() => loadSavedFilter(filter.id)}
                                        title={filter.name}
                                    >
                                        {showTruncated ? truncateText(filter.name) : filter.name}
                                    </span>
                                    <button
                                        className="rd-btn rd-btn--outline rd-btn--error rd-btn--medium"
                                        type="button"
                                        onClick={() => handleDeleteFilter(filter.id)}
                                    >
                                        <TrashIcon/>
                                    </button>
                                </div>
                            ))}

                            {visibleFilters.length === 0 && (
                                <div className="qf-save-dropdown__empty">
                                    {savedFilters.length === 0 ? 'Нет избранных фильтров' : 'Ничего не найдено'}
                                </div>
                            )}
                        </div>

                        <div className="qf-save-dropdown__divider" />

                        <div className="qf-save-dropdown__input-group">
                            <input
                                type="text"
                                className="qf-save-dropdown__input"
                                placeholder="Название фильтра"
                                id="filterNameInput"
                                value={savedFilterName}
                                onChange={(e) => setSavedFilterName(e.target.value)}
                            />
                            <button
                                className="rd-btn rd-btn--outline rd-btn--primary rd-btn--medium"
                                type="button"
                                onClick={handleClickSave}
                            >
                                <SaveIcon /> Сохранить
                            </button>
                        </div>
                        <div className="qf-save-dropdown__errors" ref={errorsRef} />
                    </div>
                )}
            </div>

            {/* Reset button */}
            <button
                type="button"
                className="qf-toolbar__icon-btn qf-toolbar__icon-btn--reset"
                title="Сбросить фильтр"
                disabled={!props.context.fieldsReady}
                onClick={() => {
                    props.context.setQuery(props.context.defaultQuery);
                    setQuickSearch("");
                    props.context.onApply(props.context.defaultQuery, "");
                    props.context.onResetActiveFilter?.();
                }}
            >
                <TrashIcon />
            </button>

            {/* Quick search */}
            <div className="qf-toolbar__search">
                <input
                    placeholder="Быстрый поиск"
                    type="text"
                    name="q"
                    value={quickSearch}
                    onChange={(e) => setQuickSearch(e.target.value)}
                    onKeyDown={handleQuickSearchKeyDown}
                />
            </div>

            {/* Apply button */}
            <button
                className="qf-toolbar__apply-btn"
                type="button"
                onClick={applyFilter}
                disabled={!canApply}
            >
                Применить
            </button>

            {/* Toggle collapse */}
            <button
                type="button"
                className={`qf-toolbar__icon-btn qf-toolbar__icon-btn--toggle${!isCollapsed ? ' qf-toolbar__icon-btn--expanded' : ''}`}
                onClick={() => props.context.handleShowRules()}
                title={isCollapsed ? 'Развернуть' : 'Свернуть'}
                disabled={!props.context.fieldsReady}
            >
                <ChevronDownIcon />
            </button>
        </>
    );
};

export default AddGroupAction;
